import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {validateEmail, validatePhoneNumber} from '../../../../../utility/validationUtil';

const inputTypesByKey = {
    text: {
        label: 'Text',
        validate: null
    },
    email: {
        label: 'Email',
        validate: validateEmail
    },
    tel: {
        label: 'Phone',
        validate: validatePhoneNumber
    }
};

const SingleLine = ({
                        index,
                        componentId,
                        fieldKey,
                        value,
                        onChange,
                        descriptionHtml,
                        onDescriptionHtmlChange,
                        commitDescriptionHtml,
                        enableValidation,
                        isEditMode,
                        isFormArchived,
                        readonly,
                        placeholderValues,
                        accessLevel,
                        submitMode,
                        type,
                        error,
                        audience,
                        recipientType,
                        linkedToStudents,
                        onEmailCheck,
                        warning,
                        setWarning,
                    }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const timeoutRef = useRef(null);
    const lastCheckedRef = useRef('');

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    useEffect(() => {
        if (!submitMode) return;
        if (readonly) return;
        if (type !== 'email') return;
        if (typeof onEmailCheck !== 'function') return;

        const emailRaw = (value ?? '').toString().trim();

        if (!emailRaw) {
            setWarning?.('');
            lastCheckedRef.current = '';
            return;
        }

        const {isValid} = validateEmail(emailRaw, false);
        if (!isValid) {
            setWarning?.('');
            lastCheckedRef.current = '';
            return;
        }

        if (emailRaw === lastCheckedRef.current) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(async () => {
            try {
                const res = await onEmailCheck(emailRaw);
                const exists = !!res?.exists;

                setWarning?.(
                    exists
                        ? 'This email already exists for this student. Submitting will update an existing contact.'
                        : ''
                );

                lastCheckedRef.current = emailRaw;
            } catch {
                setWarning?.('');
                lastCheckedRef.current = '';
            }
        }, 350);

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [submitMode, readonly, type, value, onEmailCheck, setWarning]);

    return (
        <Box key={`SingleLine_${componentId}`} sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            placeholderValues={placeholderValues}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />

                        <Stack flexDirection="row" justifyContent="space-between" gap={3} mb={2}>
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived || !!fieldKey}
                            />
                        </Stack>

                        <FormControl fullWidth>
                            <InputLabel id={`input-type-select-label-${componentId}`}>Input type</InputLabel>
                            <Select
                                labelId={`input-type-select-label-${componentId}`}
                                id={`input-type-select-${componentId}`}
                                value={type}
                                disabled={!!fieldKey}
                                defaultValue="text"
                                label="Input type"
                                onChange={(e) => onChange({
                                    index,
                                    inputType: e.target.value
                                })}
                            >
                                {Object.entries(inputTypesByKey).map(([key, config]) => (
                                    <MenuItem key={key} value={key}>
                                        {config.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readonly={readonly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />

                        {!readonly
                            ? (
                                <>
                                    <TextField
                                        fullWidth
                                        value={value ?? ''}
                                        required={enableValidation}
                                        disabled={!submitMode}
                                        helperText={error || warning || ''}
                                        error={!!error}
                                        onChange={e => onChange({
                                            id: componentId,
                                            value: e.target.value
                                        })}
                                        type={type || 'text'}
                                    />
                                </>
                            )
                            : (
                                <Typography
                                    sx={{
                                        width: {
                                            xs: '80vw',
                                            sm: '60vw',
                                            md: '45vw',
                                            lg: '45vw',
                                            xl: '45vw'
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word'
                                    }}
                                >
                                    {value}
                                </Typography>
                            )}
                    </>
                )}
        </Box>
    );
};

SingleLine.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    type: PropTypes.string,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    onEmailCheck: PropTypes.func,
    warning: PropTypes.string,
    setWarning: PropTypes.func,
};

export default SingleLine;
